'use client';

import { Close as CancelIcon } from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import { Grid } from '@mui/material';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useApolloClient, useMutation, useQuery } from '@parkchirp/api-client';
import { Dayjs } from 'dayjs';
import { ConvertReadableFeeCalculationTypeEnum, FeeTypeEnum } from 'parkchirp-shared/enums';
import React, { Dispatch, FC, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { CREATE_FEE, CREATE_RATE_V2, UPDATE_RATE_V2 } from '../../../mutations';
import {
	GET_DEFAULT_RATE_TAXES,
	GET_PRODUCT_CODES,
	GET_RATE_CODES,
	LIST_BILLING_PROFILE_NAMES_V2,
	LIST_DEFAULT_RATES_FROM_VIEW_V2,
	LIST_FEES_V2,
	LIST_RATES_FROM_VIEW_V2,
	LIST_TAX_TABLE_TAXES_V2,
} from '../../../queries';
import {
	AccessDeviceFeesForRates,
	BillingProfile,
	FacilityV2,
	FeeSelection,
	FeesForRates,
	FeeV2,
	ProductCode,
	PromoCode,
	RateCode,
	RateV2,
	SalesLimit,
	SnackBarProps,
	TaxTableTax,
} from '../../../types';
import { dayjs } from '../../../utils';
import AccessSystem from './accessSystem';
import BillingAndRates from './billingAndRates';
import ChildRateConfig from './childRateConfig';
import FacilityTab from './facilityTab';
import Fees from './fees';
import General from './general';
import Sales from './sales';

interface QueryVariables {
	limit: number;
	offset: number;
	order: any;
	filters: any[];
}

interface Props {
	selectedRate: RateV2 | null;
	setShowDialog: Dispatch<SetStateAction<ReactNode | null>>;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
	queryVariables: QueryVariables;
	refetchParkers?: Function;
	isChild: boolean;
	isDefault?: boolean;
	theme: any;
}

const defaultAccessDevices = [
	{ name: 'AVI Transponder', disabled: false, id: 1 },
	{ name: 'Barcode', disabled: false, id: 2 },
	{ name: 'Hang Tag', disabled: false, id: 4 },
	{ name: 'Proximity Card', disabled: false, id: 6 },
	{ name: 'Tenant Access Card', disabled: false, id: 7 },
	{ name: 'Windshield Sticker', disabled: false, id: 8 },
];

const RateForm: FC<Props> = ({
	selectedRate,
	setShowDialog,
	queryVariables,
	setShowSnackbar,
	refetchParkers,
	isChild,
	theme,
	isDefault = false,
}) => {
	const [tabValue, setTabValue] = useState<string>('1');
	const [activeStep, setActiveStep] = useState<number>(0);
	const client = useApolloClient();
	const [loadingBillingProfiles, setLoadingBillingProfiles] = useState<boolean>(false);
	const [loadingFees, setLoadingFees] = useState<boolean>(false);
	const [rateCodes, setRateCodes] = useState<Partial<RateCode>[]>([]);
	const [productCodes, setProductCodes] = useState<Partial<ProductCode>[]>([]);

	const queries = useMemo(
		() =>
			isDefault
				? [
						{
							query: LIST_DEFAULT_RATES_FROM_VIEW_V2,
							variables: queryVariables,
							context: { apiVersion: 'v2' },
						},
						'listDefaultRatesFromView',
					]
				: [
						{
							query: LIST_RATES_FROM_VIEW_V2,
							variables: queryVariables,
							context: { apiVersion: 'v2' },
						},
						'listRatesFromView',
					],
		[isDefault, queryVariables],
	);

	const [createNewRate, { loading: submitRateLoading }] = useMutation(CREATE_RATE_V2, {
		refetchQueries: queries,
		context: {
			apiVersion: 'v2',
		},
		awaitRefetchQueries: true,
	});

	// we don't want to fetch rates if we are updating the "Rate Name" column
	const updateRateRefetchQueries = useMemo(
		() => (typeof refetchParkers === 'function' ? [] : queries),
		[refetchParkers, queries],
	);

	const [updateRate, { loading: updateRateLoading }] = useMutation(UPDATE_RATE_V2, {
		refetchQueries: updateRateRefetchQueries,
		context: {
			apiVersion: 'v2',
		},
		awaitRefetchQueries: true,
	});

	const [createFee, { loading: createFeeLoading }] = useMutation(CREATE_FEE, {
		context: {
			apiVersion: 'v2',
		},
	});

	// facility
	const [selectedSublotId, setSelectedSublotId] = useState<number | null>(null);

	// general
	const [rateName, setRateName] = useState<string>('');
	const [passType, setPassType] = useState<string>('');
	const [location, setLocation] = useState<string>('');
	const [accessHours, setAccessHours] = useState<string>('');
	const [status, setStatus] = useState<number>(1);
	const [online, setOnline] = useState<string>(null);
	const [description, setDescription] = useState<string>('');
	const [makeModelPlate, setMakeModelPlate] = useState<string>('');
	const [missingFields, setMissingFields] = useState<string>('');
	const [facility, setFacility] = useState<FacilityV2 | null>(null);
	const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
	const [onlineStart, setOnlineStart] = useState<any>();
	const [onlineEnd, setOnlineEnd] = useState<any>(null);
	const [integrations, setIntegrations] = useState<string[]>([]);
	const [soldOut, setSoldOut] = useState<boolean>(false);

	// billing and rates
	const [FMPDay, setFMPDay] = useState<number>(15);
	const [firstMonthProration, setFirstMonthProration] = useState<string>('');
	const [billingProfile, setBillingProfile] = useState<any | null>(null);
	const [billingProfileOptions, setBillingProfileOptions] = useState<BillingProfile[]>([]);
	const [defaultRateTax1, setDefaultRateTax1] = useState<TaxTableTax>(null);
	const [defaultRateTax2, setDefaultRateTax2] = useState<TaxTableTax>(null);
	const [defaultRateTax3, setDefaultRateTax3] = useState<TaxTableTax>(null);
	const [defaultRateTax4, setDefaultRateTax4] = useState<TaxTableTax>(null);
	const [defaultRateCode, setDefaultRateCode] = useState<RateCode>(null);
	const [defaultProductCode, setDefaultProductCode] = useState<ProductCode>(null);
	const [fixedFeeName, setFixedFeeName] = useState<string>('');
	const [fixedFeeNetAmount, setFixedFeeNetAmount] = useState<number | null>(null);

	// Fee Selections
	const [monthlyFee, setMonthlyFee] = useState<FeeSelection | null>(null);
	const [activationFee, setActivationFee] = useState<FeeSelection | null>(null);
	const [transactionFee, setTransactionFee] = useState<FeeSelection | null>(null);
	const [accessDeviceFees, setAccessDeviceFees] = useState<AccessDeviceFeesForRates[]>([]);
	const [lateFee, setLateFee] = useState<FeeSelection | null>(null);
	const [lateFeeDay, setLateFeeDay] = useState<number | null>(null);

	// fees
	const [feeOptions, setFeeOptions] = useState<FeesForRates[]>([]);
	const [accessDeviceMessage, setAccessDeviceMessage] = useState<string>('');

	// sales
	const [monthsInAdvance, setMonthsInAdvance] = useState<number>(3);
	const [salesLimits, setSalesLimits] = useState<SalesLimit[]>([
		{ month: dayjs().format('MMM'), year: Number(dayjs().format('YYYY')), limit: 300 },
	]);

	// access system
	const [accessLevel, setAccessLevel] = useState<string>('');

	// Child Config
	const [childStartDate, setChildStartDate] = useState<Date | string>(new Date());
	const [childEndDate, setChildEndDate] = useState<Date | string | null>(null);
	const [childDiscountType, setChildDiscountType] = useState<string>('FEE');
	const [childDiscountAmount, setChildDiscountAmount] = useState<string | number | null>(null);
	const [futureDiscounts, setFutureDiscounts] = useState([]);

	const [availableAccessDeviceTypes, setAvailableAccessDeviceTypes] = useState(defaultAccessDevices);

	const [originalParentRateMonthlyFee, setOriginalParentRateMonthlyFee] = useState<FeeV2 | null>(null);

	const timezone = selectedRate?.billingProfile?.facility?.timezone || facility?.timezone || 'UTC';

	const facilityOnlineStartDate = useMemo(
		() => (facility?.onlineStartDate ? new Date(facility.onlineStartDate).toISOString().split('T')[0] : null),
		[facility],
	);

	const facilityOnlineEndDate = useMemo(
		() =>
			facility?.onlineEndDate
				? dayjs(new Date(facility.onlineEndDate).toISOString().split('T')[0], 'YYYY-MM-DD')
				: null,
		[facility],
	);

	const { data: taxTableTaxesData } = useQuery(LIST_TAX_TABLE_TAXES_V2, {
		context: {
			apiVersion: 'v2',
		},
		variables: {
			facilityId: facility?.id,
		},
		skip: !isDefault || !facility,
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});
	// Called when creating a rate
	useEffect(() => {
		if (facility) {
			const fetch = async () => {
				setLoadingFees(true);
				setLoadingBillingProfiles(true);
				const billingProfilesFetch = await client.query({
					query: LIST_BILLING_PROFILE_NAMES_V2,
					variables: {
						facilityId: facility.id,
					},
					context: {
						apiVersion: 'v2',
					},
					fetchPolicy: 'network-only',
				});

				const feesFetch = await client.query({
					query: LIST_FEES_V2,
					context: {
						apiVersion: 'v2',
					},
					variables: {
						facilityId: facility.id,
					},
					fetchPolicy: 'network-only',
				});

				const productCodesFetch = await client.query({
					query: GET_PRODUCT_CODES,
					variables: {
						organizationId: facility.organizationId,
					},
					context: {
						apiVersion: 'v2',
					},
					fetchPolicy: 'network-only',
				});
				const rateCodesFetch = await client.query({
					query: GET_RATE_CODES,
					variables: {
						organizationId: facility.organizationId,
					},
					context: {
						apiVersion: 'v2',
					},
					fetchPolicy: 'network-only',
				});
				setProductCodes(productCodesFetch.data.getProductCodesByOrganizationId);
				setRateCodes(rateCodesFetch.data.getRateCodesByOrganizationId);

				const feesData = feesFetch.data.listFees;

				if (billingProfilesFetch.data.listBillingProfileNames.length === 0) {
					setShowSnackbar({
						message: `${facility.facilityName} has no available billing profiles. Please create one first.`,
						severity: 'error',
					});
				}
				if (isDefault) {
					setOnline('ADMIN_SIGN_UP_ONLY');
				} else if (
					feesData.filter((fee) => fee.feeType.name === 'Monthly').length === 0 &&
					!facility.facilitySettings?.usesDefaultRates
				) {
					setShowSnackbar({
						message: `${facility.facilityName} has no available monthly fees. Please create one first.`,
						severity: 'error',
					});
				}

				const billingProfilesData = billingProfilesFetch.data.listBillingProfileNames;
				setBillingProfileOptions(billingProfilesData);
				setFeeOptions(feesData.map((f) => ({ ...f, disabled: false })));
				setLoadingBillingProfiles(false);
				if (!selectedRate) {
					// Set the onlineStartDate whenever we first open the modal
					if (facilityOnlineStartDate) {
						if (dayjs(facilityOnlineStartDate).isSameOrBefore(dayjs())) {
							setOnlineStart(dayjs().startOf('day'));
						} else {
							setOnlineStart(dayjs(facilityOnlineStartDate).startOf('day'));
						}
					} else {
						setOnlineStart(dayjs().startOf('day'));
					}
					if (facilityOnlineEndDate) {
						setOnlineEnd(facilityOnlineEndDate.startOf('day'));
					}
					const dates = [];
					let startDate = dayjs().tz(timezone).format('MMM YYYY');
					const endDate = dayjs(startDate, 'MMM YYYY').add(12, 'month').format('MMM YYYY');
					while (dayjs(startDate, 'MMM YYYY') < dayjs(endDate, 'MMM YYYY')) {
						dates.push({
							month: startDate.split(' ')[0],
							year: startDate.split(' ')[1],
							limit: isDefault ? 1 : 300,
						});
						startDate = dayjs(startDate, 'MMM YYYY').add(1, 'month').format('MMM YYYY');
					}
					setSalesLimits(dates);
				}
				setLoadingFees(false);
			};
			fetch();
		}
	}, [
		facility,
		facilityOnlineStartDate,
		selectedRate,
		client,
		setShowSnackbar,
		timezone,
		isDefault,
		facilityOnlineEndDate,
	]);

	// Called when editing a rate or creating/editing a child rate
	useEffect(() => {
		if (selectedRate) {
			const fetch = async () => {
				const { data } = await client.query({
					query: GET_DEFAULT_RATE_TAXES,
					context: {
						apiVersion: 'v2',
					},
					variables: {
						rateId: selectedRate.id,
						facilityId: selectedRate.billingProfile.facility.id,
					},
					fetchPolicy: 'network-only',
				});
				setDefaultRateTax1(data.getDefaultRateTaxes?.defaultRateTax1);
				setDefaultRateTax2(data.getDefaultRateTaxes?.defaultRateTax2);
				setDefaultRateTax3(data.getDefaultRateTaxes?.defaultRateTax3);
				setDefaultRateTax4(data.getDefaultRateTaxes?.defaultRateTax4);
				setDefaultRateCode(data.getDefaultRateTaxes?.rateCode);
				setDefaultProductCode(data.getDefaultRateTaxes?.productCode);
			};
			if (isDefault) {
				fetch();
			}
			if (!selectedRate.parentRateId && isChild) {
				setRateName('');
			} else {
				setRateName(selectedRate.name);
			}
			setSoldOut(selectedRate.isSoldOut);
			if (selectedRate.parentRateId) {
				const { startDate, endDate, discountType, discountAmount } = selectedRate.childRateMetadata;
				setChildStartDate(dayjs(startDate).utc().format('MM/DD/YYYY'));
				if (endDate) {
					setChildEndDate(dayjs(endDate).utc().format('MM/DD/YYYY'));
				}
				setChildDiscountType(discountType);
				let adjustedDiscountAmount;
				if (discountType === 'DOLLAR') {
					adjustedDiscountAmount = discountAmount / 100;
				} else if (discountType === 'PERCENT') {
					adjustedDiscountAmount = discountAmount / 10000;
				} else {
					adjustedDiscountAmount = discountAmount;
				}
				setChildDiscountAmount(adjustedDiscountAmount);
			}
			setActiveStep(1);
			setTabValue('2');
			setPassType(selectedRate.passType);
			setLocation(selectedRate.location);
			setAccessHours(
				selectedRate.accessHours === 'TWENTYFOUR_SEVEN_ACCESS'
					? 'TWENTY_FOUR_SEVEN_ACCESS'
					: selectedRate.accessHours,
			);
			setStatus(selectedRate.active ? 1 : 0);
			setOnline(selectedRate.display);
			setDescription(selectedRate.description);
			setMakeModelPlate(selectedRate.requireCarInfo);
			setLateFeeDay(selectedRate.lateFeeDay);
			setFMPDay(selectedRate.firstMonthProration === 'HALF_MONTH' ? selectedRate.firstMonthProrationDay : 15);
			setFirstMonthProration(selectedRate.firstMonthProration);
			setSelectedSublotId(selectedRate.sublot.id);
			setBillingProfile({
				id: selectedRate.billingProfileId,
				name: selectedRate.billingProfile.name,
				paymentDueDay: selectedRate.billingProfile.paymentDueDay,
			});
			selectedRate.rateFees.forEach((rf) => {
				if (rf.fee.feeType.name === 'Activation') {
					setActivationFee({ ...rf.fee, id: rf.feeId });
				}
				if (rf.fee.feeType.name === 'Monthly') {
					setMonthlyFee({ ...rf.fee, id: rf.feeId });
					setOriginalParentRateMonthlyFee(rf.fee);
				}
				if (rf.fee.feeType.name === 'Transaction') {
					setTransactionFee({ ...rf.fee, id: rf.feeId });
				}
				if (rf.fee.feeType.name === 'Late' || rf.fee.feeType.name === 'Recurring Late') {
					setLateFee({ ...rf.fee, id: rf.feeId });
				}
			});
			setAccessDeviceFees(
				selectedRate.rateAccessDevices.map((rad) => ({
					id: rad.feeId,
					required: rad.required,
					feeName: rad.fee?.feeName,
					feeTypeId: rad.fee?.feeTypeId,
					feeType: { name: rad.fee?.feeType.name },
					feeAccessDeviceType: {
						accessDeviceTypeId: rad.accessDeviceTypeId,
					},
					feesHistory: rad.fee?.feesHistory,
					type: rad.accessDeviceType.type,
				})),
			);
			setAccessDeviceMessage(selectedRate.accessDeviceMessage);
			const sortedSalesLimits = [...selectedRate.salesLimits].sort((a, b) => {
				if (a.year !== b.year) {
					return a.year - b.year;
				}
				const monthsOrder = [
					'Jan',
					'Feb',
					'Mar',
					'Apr',
					'May',
					'Jun',
					'Jul',
					'Aug',
					'Sep',
					'Oct',
					'Nov',
					'Dec',
				];
				return monthsOrder.indexOf(a.month) - monthsOrder.indexOf(b.month);
			});
			setSalesLimits(sortedSalesLimits.map((sl) => ({ month: sl.month, year: sl.year, limit: sl.limit })));
			setPromoCodes(
				selectedRate.promoCodes.map((pc: any) => ({
					allowedUses: pc.useLimit,
					code: pc.text,
					expiresOn: pc.expiresOn,
				})),
			);
			setIntegrations(selectedRate.externalApiRates.map(({ integration }) => integration));
			setFacility(selectedRate.billingProfile.facility);
			setAccessLevel(selectedRate.accessLevelCode);
			if (
				(selectedRate?.onlineStartDate && !isChild) ||
				(selectedRate?.parentRateId && selectedRate?.onlineStartDate)
			) {
				setOnlineStart(dayjs(selectedRate.onlineStartDate).tz(timezone));
			} else {
				setOnlineStart(dayjs().tz(timezone));
			}

			if ((selectedRate.onlineEndDate && !isChild) || (selectedRate.parentRateId && selectedRate.onlineEndDate)) {
				setOnlineEnd(dayjs(selectedRate.onlineEndDate).tz(timezone));
			}
			setFutureDiscounts(
				selectedRate.futureChildRateDiscounts.map((discount) => ({
					...discount,
					originalEndDatePassed:
						discount.endDate &&
						dayjs.utc(discount.endDate).format('YYYY-MM-DD') < dayjs().tz(timezone).format('YYYY-MM-DD'),
					discountAmount:
						discount.discountType === 'PERCENT'
							? discount.discountAmount / 10000
							: discount.discountAmount / 100,
				})),
			);
		}
	}, [facility, isChild, selectedRate, timezone]);

	const steps = useMemo(
		() =>
			isChild
				? [
						'Facility',
						'General Settings',
						'Child Configuration',
						'Billing & Rates',
						'Fees',
						'Sales',
						'Access System',
					]
				: ['Facility', 'General Settings', 'Billing & Rates', 'Fees', 'Sales', 'Access System'],
		[isChild],
	);

	const clearModal = useCallback(() => {
		setShowDialog(null);
		setRateName('');
		setPassType('');
		setLocation('');
		setAccessHours('');
		setStatus(null);
		setOnline('');
		setDescription('');
		setMakeModelPlate('');
		setFMPDay(15);
		setFirstMonthProration('');
		setBillingProfile(null);
		setActivationFee(null);
		setTransactionFee(null);
		setLateFee(null);
		setLateFeeDay(null);
		setAccessDeviceFees([]);
		setAccessDeviceMessage('');
		setMonthsInAdvance(3);
		setSalesLimits([{ month: dayjs().format('MMM'), year: Number(dayjs().format('YYYY')), limit: 300 }]);
		setAccessLevel('');
		setFacility(null);
		setBillingProfileOptions([]);
		setTabValue('1');
		setActiveStep(0);
		setIntegrations([]);
		setOnlineStart(null);
		setOnlineEnd(null);
	}, [setShowDialog]);

	const convertSelectedDateToFacilityTimezoneDate = useCallback(
		(date: Date | string) => {
			const initial = dayjs(date).tz(facility?.timezone, true);
			return dayjs(initial).tz('UTC');
		},
		[facility],
	);

	const generateFixedFeeHistory = useCallback(() => {
		const fixedFeeHistory = originalParentRateMonthlyFee?.feesHistory
			? originalParentRateMonthlyFee.feesHistory.map((fh) => ({
					tax1: fh.tax1 || 0,
					tax2: fh.tax2 || 0,
					tax3: fh.tax3 || 0,
					tax4: fh.tax4 || 0,
					price: fixedFeeNetAmount * 100,
					rateCodeId: Number(fh.rateCodeId) || null,
					productCodeId: Number(fh.productCodeId) || null,
					startDate: dayjs
						.tz(childStartDate, facility?.timezone || 'UTC')
						.startOf('day')
						.utc()
						.toISOString(),
					inactiveDate: null,
					taxCalculation: fh.taxCalculation,
					tax1TaxTableId: fh.tax1TaxTableId || null,
					tax2TaxTableId: fh.tax2TaxTableId || null,
					tax3TaxTableId: fh.tax3TaxTableId || null,
					tax4TaxTableId: fh.tax4TaxTableId || null,
					inputType: 'Net',
				}))
			: [];

		return fixedFeeHistory;
	}, [fixedFeeNetAmount, originalParentRateMonthlyFee, childStartDate, facility]);

	const submitRate = useCallback(async () => {
		type RateFeeIds = {
			feeId: string;
		};

		let discountAmount = null;
		let fixedMonthlyFeeId: string = null;

		if (childDiscountType && isChild) {
			if (childDiscountType === 'PERCENT') {
				discountAmount = Number(childDiscountAmount) * 10000;
			} else if (childDiscountType === 'DOLLAR') {
				discountAmount = Number(childDiscountAmount) * 100;
			}
		}

		if (fixedFeeName && fixedFeeNetAmount && monthlyFee?.id === originalParentRateMonthlyFee?.id) {
			try {
				const { data } = await createFee({
					variables: {
						feeName: fixedFeeName,
						feeTypeId: 9, // Monthly Fee Type ID
						type: FeeTypeEnum.MONTHLY.toUpperCase(),
						facilityId: facility.id,
						calculationType: ConvertReadableFeeCalculationTypeEnum['Dollar Per Parker'], // Monthly Fees are always Dollar Per Parker
						feesHistory: generateFixedFeeHistory(),
						accessDeviceTypeId: null,
						isChildRateFixedFee: true,
					},
				});

				fixedMonthlyFeeId = data.createFee;
			} catch (error) {
				setShowSnackbar({ message: error.message, severity: 'error' });
				throw error;
			}
		}

		const rateFeeIds: RateFeeIds[] = []; // isDefault = empty array

		if (fixedMonthlyFeeId) {
			rateFeeIds.push({ feeId: fixedMonthlyFeeId });
		} else if (!isDefault) {
			rateFeeIds.push({ feeId: monthlyFee.id });
		}

		const rateAccessDevices = [];
		if (activationFee?.id) {
			rateFeeIds.push({ feeId: activationFee.id });
		}
		if (transactionFee?.id) {
			rateFeeIds.push({ feeId: transactionFee.id });
		}
		if (lateFee?.id) {
			rateFeeIds.push({ feeId: lateFee.id });
		}
		if (accessDeviceFees.length > 0) {
			accessDeviceFees.forEach((adf: any) => {
				rateAccessDevices.push({
					feeId: adf.id,
					required: !!adf.required,
					accessDeviceTypeId: adf.feeAccessDeviceType.accessDeviceTypeId,
				});
				if (adf.id) {
					rateFeeIds.push({ feeId: adf.id });
				}
			});
		}

		let active = status;
		if (isChild && dayjs(childStartDate).isAfter(dayjs())) {
			active = 0;
		}
		const rateVariables = {
			billingProfileId: billingProfile.id,
			name: rateName,
			description,
			active: active !== 0,
			display: online,
			passType,
			accessHours,
			location,
			requireCarInfo: makeModelPlate || 'OPTIONAL',
			firstMonthProration,
			firstMonthProrationDay: FMPDay,
			taxExempt: false,
			accessDeviceMessage,
			facilityId: facility.id,
			monthsToSellInAdvance: monthsInAdvance,
			soldOut,
			onlineStartDate:
				onlineStart && online !== 'ADMIN_SIGN_UP_ONLY'
					? convertSelectedDateToFacilityTimezoneDate(onlineStart)
					: null,
			onlineEndDate:
				onlineEnd && online !== 'ADMIN_SIGN_UP_ONLY'
					? convertSelectedDateToFacilityTimezoneDate(onlineEnd)
					: null,
			accessSystemMeta: {
				accessLevelCode: accessLevel,
			},
			lateFeeDay,
			rateFeeIds,
			rateAccessDevices,
			accessLevelCode: accessLevel,
			promoCodes: promoCodes.map((pc) => ({
				useLimit: pc.allowedUses,
				text: pc.code,
				expiresOn: pc.expiresOn,
				useCount: 0,
			})),
			salesLimits: salesLimits.map((sl) => ({
				...sl,
				year: Number(sl.year),
				limitTimestamp: dayjs(`${sl.year}-${sl.month}-01`),
			})),
			parentRateId: isChild ? selectedRate.id : null,
			sublotId: selectedSublotId,
			integrations,
			isDefault,
			defaultRateTax1: defaultRateTax1?.id || null,
			defaultRateTax2: defaultRateTax2?.id || null,
			defaultRateTax3: defaultRateTax3?.id || null,
			defaultRateTax4: defaultRateTax4?.id || null,
			defaultRateCode: Number(defaultRateCode?.id) || null,
			defaultProductCode: Number(defaultProductCode?.id) || null,
			futureChildRateDiscounts: isChild
				? futureDiscounts.map((discount) => ({
						id: discount.id,
						startDate: dayjs.utc(discount.startDate).format('YYYY-MM-DD'),
						endDate: discount.endDate ? dayjs.utc(discount.endDate).format('YYYY-MM-DD') : null,
						discountType: discount.discountType,
						discountAmount:
							discount.discountType === 'PERCENT'
								? Math.round(Number(discount.discountAmount) * 10000)
								: Math.round(Number(discount.discountAmount) * 100),
					}))
				: [],
		};

		if (selectedRate && !isChild) {
			try {
				await updateRate({
					variables: {
						...rateVariables,
						id: selectedRate.id,
					},
				});
				if (typeof refetchParkers === 'function') {
					await refetchParkers();
				}
				setShowSnackbar({ message: 'Successfully updated rate', severity: 'success' });
				clearModal();
			} catch (err) {
				setShowSnackbar({ message: err.message, severity: 'error' });
			}
		} else if (selectedRate?.parentRateId) {
			try {
				await updateRate({
					variables: {
						...rateVariables,
						id: selectedRate.id,
						parentRateId: isChild ? selectedRate.id : null,
						childRate: {
							startDate: dayjs.utc(childStartDate).format('YYYY-MM-DD'),
							endDate: childEndDate ? dayjs.utc(childEndDate).format('YYYY-MM-DD') : null,
							discountType: childDiscountType,
							discountAmount,
						},
					},
				});
				if (typeof refetchParkers === 'function') {
					await refetchParkers();
				}
				setShowSnackbar({ message: 'Successfully updated rate', severity: 'success' });
				clearModal();
			} catch (err) {
				setShowSnackbar({ message: err.message, severity: 'error' });
			}
		} else {
			try {
				await createNewRate({
					variables: {
						...rateVariables,
						parentRateId: isChild ? selectedRate.id : null,
						childRate: {
							startDate: dayjs(childStartDate).toISOString(),
							endDate: childEndDate ? dayjs(childEndDate).toISOString() : null,
							discountType: childDiscountType,
							discountAmount,
						},
					},
				});
				setShowSnackbar({ message: 'Successfully created rate', severity: 'success' });
				clearModal();
			} catch (err) {
				setShowSnackbar({ message: err.message, severity: 'error' });
			}
		}
	}, [
		FMPDay,
		accessDeviceFees,
		accessDeviceMessage,
		accessHours,
		accessLevel,
		activationFee,
		billingProfile,
		childDiscountAmount,
		childDiscountType,
		childEndDate,
		childStartDate,
		clearModal,
		convertSelectedDateToFacilityTimezoneDate,
		createNewRate,
		description,
		facility,
		firstMonthProration,
		integrations,
		isChild,
		lateFee,
		lateFeeDay,
		location,
		makeModelPlate,
		monthlyFee,
		monthsInAdvance,
		online,
		onlineEnd,
		onlineStart,
		passType,
		promoCodes,
		rateName,
		refetchParkers,
		salesLimits,
		selectedRate,
		selectedSublotId,
		setShowSnackbar,
		status,
		transactionFee,
		updateRate,
		isDefault,
		defaultRateTax1?.id,
		defaultRateTax2?.id,
		defaultRateTax3?.id,
		defaultRateTax4?.id,
		defaultRateCode,
		defaultProductCode,
		futureDiscounts,
		fixedFeeName,
		fixedFeeNetAmount,
		createFee,
		generateFixedFeeHistory,
		originalParentRateMonthlyFee,
		soldOut,
	]);

	const minOnlineStartDate = useMemo((): Dayjs => {
		if (facilityOnlineStartDate && dayjs(facilityOnlineStartDate).isAfter(dayjs())) {
			return dayjs(facilityOnlineStartDate).startOf('day');
		}
		return dayjs().startOf('day');
	}, [facilityOnlineStartDate]);

	return (
		<div style={{ width: '1200px' }} data-cy='rate-modal-container'>
			<TabContext value={tabValue}>
				<Grid container spacing={2}>
					<Grid xs={11} item>
						<h3>
							{isDefault && selectedRate
								? 'Edit Default Rate'
								: isDefault
									? 'Add Default Rate'
									: selectedRate && !isChild
										? 'Edit Rate'
										: selectedRate?.parentRateId
											? 'Edit Child Rate'
											: isChild
												? 'Add Child Rate'
												: 'Add Rate'}
						</h3>
					</Grid>
					<Grid style={{ textAlign: 'right' }} xs={1} item>
						<CancelIcon
							className='pca__ratesForm-icon'
							onClick={clearModal}
							data-cy='close-modal-icon-button'
						/>
					</Grid>
				</Grid>
				<Stepper activeStep={activeStep} alternativeLabel data-cy='rates-stepper'>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
				<TabPanel value='1'>
					<FacilityTab
						selectedRate={selectedRate}
						missingFields={missingFields}
						setMissingFields={setMissingFields}
						setTabValue={setTabValue}
						setFacility={setFacility}
						facility={facility}
						clearModal={clearModal}
						setActiveStep={setActiveStep}
						loadingBillingProfiles={loadingBillingProfiles}
						feeOptions={feeOptions}
						selectedSublotId={selectedSublotId}
						setSelectedSublotId={setSelectedSublotId}
						theme={theme}
						isDefault={isDefault}
						hasBillingProfiles={!!billingProfileOptions.length}
					/>
				</TabPanel>
				<TabPanel value='2'>
					<General
						loadingFees={loadingFees}
						rateName={rateName}
						setRateName={setRateName}
						status={status}
						location={location}
						setLocation={setLocation}
						passType={passType}
						setPassType={setPassType}
						accessHours={accessHours}
						setAccessHours={setAccessHours}
						online={online}
						setOnline={setOnline}
						description={description}
						setDescription={setDescription}
						makeModelPlate={makeModelPlate}
						setMakeModelPlate={setMakeModelPlate}
						setTabValue={setTabValue}
						setMissingFields={setMissingFields}
						missingFields={missingFields}
						promoCodes={promoCodes}
						setPromoCodes={setPromoCodes}
						onlineStart={onlineStart}
						setOnlineStart={setOnlineStart}
						onlineEnd={onlineEnd}
						setOnlineEnd={setOnlineEnd}
						selectedRate={selectedRate}
						setActiveStep={setActiveStep}
						isChild={isChild}
						integrations={integrations}
						setIntegrations={setIntegrations}
						minOnlineStartDate={minOnlineStartDate}
						facilityOnlineStartDate={facilityOnlineStartDate}
						facilityOnlineEndDate={facilityOnlineEndDate}
						timezone={timezone}
						theme={theme}
						isDefault={isDefault}
						facilityIntegration={facility?.integration}
						soldOut={soldOut}
						setSoldOut={setSoldOut}
					/>
				</TabPanel>
				{isChild && (
					<TabPanel value='3'>
						<ChildRateConfig
							selectedRate={selectedRate}
							setTabValue={setTabValue}
							childStartDate={childStartDate}
							setChildStartDate={setChildStartDate}
							childEndDate={childEndDate}
							setChildEndDate={setChildEndDate}
							childDiscountType={childDiscountType}
							setChildDiscountType={setChildDiscountType}
							childDiscountAmount={childDiscountAmount}
							setChildDiscountAmount={setChildDiscountAmount}
							setActiveStep={setActiveStep}
							missingFields={missingFields}
							setMissingFields={setMissingFields}
							setMonthlyFee={setMonthlyFee}
							theme={theme}
							onlineStart={onlineStart}
							onlineEnd={onlineEnd}
							setShowSnackbar={setShowSnackbar}
							online={online}
							futureDiscounts={futureDiscounts}
							setFutureDiscounts={setFutureDiscounts}
						/>
					</TabPanel>
				)}
				<TabPanel value={isChild ? '4' : '3'}>
					<BillingAndRates
						selectedRate={selectedRate}
						FMPDay={FMPDay}
						setFMPDay={setFMPDay}
						firstMonthProration={firstMonthProration}
						setFirstMonthProration={setFirstMonthProration}
						billingProfile={billingProfile}
						setBillingProfile={setBillingProfile}
						billingProfileOptions={billingProfileOptions}
						missingFields={missingFields}
						setMissingFields={setMissingFields}
						setTabValue={setTabValue}
						setActiveStep={setActiveStep}
						feeOptions={feeOptions}
						monthlyFee={monthlyFee}
						setMonthlyFee={setMonthlyFee}
						isChild={isChild}
						childDiscountType={childDiscountType}
						theme={theme}
						isDefault={isDefault}
						defaultRateTax1={defaultRateTax1}
						defaultRateTax2={defaultRateTax2}
						defaultRateTax3={defaultRateTax3}
						defaultRateTax4={defaultRateTax4}
						setDefaultRateTax1={setDefaultRateTax1}
						setDefaultRateTax2={setDefaultRateTax2}
						setDefaultRateTax3={setDefaultRateTax3}
						setDefaultRateTax4={setDefaultRateTax4}
						taxTableOptions={taxTableTaxesData?.listTaxTableTaxes || []}
						fixedFeeName={fixedFeeName}
						setFixedFeeName={setFixedFeeName}
						fixedFeeNetAmount={fixedFeeNetAmount}
						setFixedFeeNetAmount={setFixedFeeNetAmount}
						rateCodes={rateCodes}
						productCodes={productCodes}
						defaultRateCode={defaultRateCode}
						setDefaultRateCode={setDefaultRateCode}
						defaultProductCode={defaultProductCode}
						setDefaultProductCode={setDefaultProductCode}
					/>
				</TabPanel>
				<TabPanel value={isChild ? '5' : '4'}>
					<Fees
						setTabValue={setTabValue}
						activationFee={activationFee}
						setActivationFee={setActivationFee}
						accessDeviceFees={accessDeviceFees}
						setAccessDeviceFees={setAccessDeviceFees}
						accessDeviceMessage={accessDeviceMessage}
						setAccessDeviceMessage={setAccessDeviceMessage}
						setFeeOptions={setFeeOptions}
						setActiveStep={setActiveStep}
						feeOptions={feeOptions}
						transactionFee={transactionFee}
						setTransactionFee={setTransactionFee}
						availableAccessDeviceTypes={availableAccessDeviceTypes}
						setAvailableAccessDeviceTypes={setAvailableAccessDeviceTypes}
						isChild={isChild}
						lateFee={lateFee}
						setLateFee={setLateFee}
						lateFeeDay={lateFeeDay}
						setLateFeeDay={setLateFeeDay}
						billingProfile={billingProfile}
						theme={theme}
						facilityIntegration={facility?.integration}
						online={online}
					/>
				</TabPanel>
				<TabPanel value={isChild ? '6' : '5'}>
					<Sales
						setTabValue={setTabValue}
						monthsInAdvance={monthsInAdvance}
						setMonthsInAdvance={setMonthsInAdvance}
						salesLimits={salesLimits}
						setSalesLimits={setSalesLimits}
						setActiveStep={setActiveStep}
						isChild={isChild}
						theme={theme}
					/>
				</TabPanel>
				<TabPanel value={isChild ? '7' : '6'}>
					<AccessSystem
						setTabValue={setTabValue}
						accessLevel={accessLevel}
						setAccessLevel={setAccessLevel}
						submitRate={submitRate}
						setActiveStep={setActiveStep}
						loading={submitRateLoading || updateRateLoading || createFeeLoading}
						isChild={isChild}
						theme={theme}
					/>
				</TabPanel>
			</TabContext>
		</div>
	);
};

export default RateForm;
